import React, { useCallback } from "react";
import {
  TableRow,
  RowRendererProps,
} from "../../../../components/PaginatedListTableRenderer";
import { unixTimeHumanReadable } from "../../../../api/connx";
import { truncateWithEllipses } from "../../../../helpers/strings";
import { RVModelOrderRow } from "./GetRVModelOrders";
import RVRowActions from "../RVRow";
import { Dropdown } from "react-bootstrap";
import { RBACPermission } from "../../../../user/rbac";

export interface OrderEvent {
  modelID: string;
  orderID: string;
}

export interface LinkEvent extends OrderEvent {
  // only present for 'unlink' events
  thingName?: string;
}

// an order to be released from its owner, or reassigned
export interface ReleaseEvent extends OrderEvent {
  name: string;
}

// an order to be assigned to a user group
export interface AssignGroupEvent extends OrderEvent {
  // the user group the order is currently assigned to
  userGroupID?: string;
}

// an order to be hidden from the Orders page and the dashboard, or shown again
export interface SetHiddenEvent extends OrderEvent {
  // whether the order is to be hidden
  hidden: boolean;
}

export interface RowProps {
  displayIDs?: boolean;
  // names of the user groups orders can be assigned to, by group ID; the
  // group of an order is only shown when there is more than one
  groupNames?: Record<string, string>;
  onAssignGroup?: (e: AssignGroupEvent) => void;
  canAssignGroup?: boolean;
  onSetHidden?: (e: SetHiddenEvent) => void;
  // only a model/order admin hides orders
  canHide?: boolean;
  onAssignOwner?: (orderID: string) => Promise<void> | void;
  onRelease?: (e: ReleaseEvent) => void;
  onDelete?: (e: OrderEvent) => boolean | Promise<boolean>;
  onEdit?: (e: OrderEvent) => void;
  onLink?: (e: LinkEvent) => void;
  canDelete?: boolean;
  canUpdate?: boolean;
  canLink?: boolean;
  canAssignOrder?: boolean;
  canRelease?: boolean;
  // show the Owner column; the owners are only looked up for a holder of
  // rv_owner_list, so it is blank for anyone else
  showOwner?: boolean;
}

// the group of an order is only of interest when there are child groups
export const showGroupColumn = (groupNames?: Record<string, string>): boolean =>
  Object.keys(groupNames || {}).length > 1;

// an order is hidden or shown by a model/order admin, or by anyone who may
// link devices to orders; as with linking, the backend also requires the
// caller to administer the order's model
export const canHideOrders = (
  granted: (p: RBACPermission) => boolean
): boolean => granted("rv_model_admin") || granted("provisioning_link_thing");

export const Row = ({
  item,
  index,
  onDeleted,
  displayIDs,
  groupNames,
  onAssignGroup,
  canAssignGroup,
  onSetHidden,
  canHide,
  onAssignOwner,
  onRelease,
  onEdit,
  onDelete,
  onLink,
  canDelete,
  canUpdate,
  canAssignOrder,
  canRelease,
  showOwner,
  canLink,
  ...childProps
}: RowProps & RowRendererProps<RVModelOrderRow>): JSX.Element => {
  const handleAssignOwner = useCallback(() => {
    onAssignOwner && onAssignOwner(item.docID);
  }, [onAssignOwner, item]);

  const canDeleteItem = canDelete && !item.provisioned && !item.iotThingName;

  // an order has one owner, so one action, as with Link/Unlink: Assign while
  // it has none, Release once it has one. When the owner is unknown
  // (undefined: no permission, or the lookup failed) Assign is offered and
  // the backend refuses it if the order turns out to be owned.
  const owned = !!item.owner;
  const handleDelete = canDeleteItem
    ? (): boolean | Promise<boolean> =>
        onDelete?.({ modelID: item.groupID, orderID: item.docID }) || false
    : undefined;

  const handleEdit = canUpdate
    ? (): void => onEdit?.({ modelID: item.groupID, orderID: item.docID })
    : undefined;

  const handleLink = (): void =>
    onLink?.({
      modelID: item.groupID,
      orderID: item.docID,
      thingName: item.iotThingName,
    });

  const handleRelease = (): void =>
    onRelease?.({
      modelID: item.groupID,
      orderID: item.docID,
      name: item.name,
    });

  const handleAssignGroup = (): void =>
    onAssignGroup?.({
      modelID: item.groupID,
      orderID: item.docID,
      userGroupID: item.userGroupID,
    });
  const showGroup = showGroupColumn(groupNames);

  // one action flips the flag: Hide while shown, Unhide once hidden
  const handleSetHidden = (): void =>
    onSetHidden?.({
      modelID: item.groupID,
      orderID: item.docID,
      hidden: !item.hidden,
    });

  return (
    <TableRow item={item} {...childProps}>
      <div>{item.name}</div>
      <div>{truncateWithEllipses(item.desc, 100)}</div>
      <div>{item.serialNumber}</div>
      <div>{unixTimeHumanReadable(item.orderDate)}</div>
      <div>{item.provisioned ? "Yes" : "No"}</div>
      <div data-testid="order-hidden">{item.hidden ? "Yes" : "No"}</div>
      {showGroup && (
        <div data-testid="order-group">
          {groupNames?.[item.userGroupID || ""] || ""}
        </div>
      )}
      {showOwner && (
        <div data-testid="order-owner">{item.owner?.email || ""}</div>
      )}
      {displayIDs && <div>{item.docID}</div>}
      <RVRowActions
        getItemName={(): string => item.name}
        onDelete={handleDelete}
        onDeleted={(): void => onDeleted(index)}
        onEdit={handleEdit}
      >
        {owned ? (
          <Dropdown.Item
            disabled={!onRelease || true !== canRelease}
            onClick={handleRelease}
          >
            Release
          </Dropdown.Item>
        ) : (
          <Dropdown.Item
            disabled={!onAssignOwner || true !== canAssignOrder}
            onClick={handleAssignOwner}
          >
            Assign
          </Dropdown.Item>
        )}
        <Dropdown.Item
          disabled={!onAssignGroup || true !== canAssignGroup || !showGroup}
          onClick={handleAssignGroup}
        >
          Set group
        </Dropdown.Item>
        <Dropdown.Item
          disabled={!onSetHidden || true !== canHide}
          onClick={handleSetHidden}
        >
          {item.hidden ? "Unhide" : "Hide"}
        </Dropdown.Item>
        <Dropdown.Item
          disabled={undefined === item.iotThingName || true !== canLink}
          onClick={handleLink}
        >
          {"" !== item.iotThingName ? "Unlink" : "Link"}
        </Dropdown.Item>
      </RVRowActions>
    </TableRow>
  );
};
